import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, MessageSquare, Share2, ChevronUp, ChevronDown, List, X, Sparkles, Lock, Crown, ShoppingCart } from 'lucide-react';
import { getComic, getEpisodes, getEpisode, likeEpisode, getComments, createComment, trackEngagement, checkEpisodeAccess, checkEpisodeAccessPublic, createChapterCheckout } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { AdBanner, ChapterEndAd } from '../components/AdBanner';
import { TipButton, SuperLikeButton } from '../components/TipButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useReadingProgress } from '../hooks/useReadingProgress';
import { toast } from 'sonner';

const ReaderPage = () => {
  const { comicId, episodeId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { saveProgress } = useReadingProgress();
  
  const [comic, setComic] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showEpisodeList, setShowEpisodeList] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pagesViewed, setPagesViewed] = useState(new Set());
  const [chapterCompleted, setChapterCompleted] = useState(false);
  const [accessDenied, setAccessDenied] = useState(null); // Holds access denial info
  const [purchasingEpisode, setPurchasingEpisode] = useState(false);
  
  const scrollContainerRef = useRef(null);
  const lastScrollY = useRef(0);
  const controlsTimeout = useRef(null);
  const engagementTracked = useRef(new Set());

  // Track engagement event (with deduplication)
  const trackEvent = useCallback(async (eventType, metadata = {}) => {
    if (!isAuthenticated || !comic?.creator_id) return;
    
    const eventKey = `${eventType}-${currentEpisode?.id || ''}-${JSON.stringify(metadata)}`;
    if (engagementTracked.current.has(eventKey)) return;
    engagementTracked.current.add(eventKey);
    
    try {
      await trackEngagement({
        creator_id: comic.creator_id,
        comic_id: comicId,
        episode_id: currentEpisode?.id,
        event_type: eventType,
        metadata
      });
    } catch (error) {
      console.debug('Engagement tracking failed:', error);
    }
  }, [isAuthenticated, comic?.creator_id, comicId, currentEpisode?.id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [comicRes, episodesRes] = await Promise.all([
          getComic(comicId),
          getEpisodes(comicId)
        ]);
        
        setComic(comicRes.data);
        setEpisodes(episodesRes.data);
        
        // Load specific episode or first episode
        const targetEpisodeId = episodeId || episodesRes.data[0]?.id;
        if (targetEpisodeId) {
          // Check access BEFORE loading episode content
          try {
            let accessRes;
            if (isAuthenticated) {
              accessRes = await checkEpisodeAccess(targetEpisodeId);
            } else {
              accessRes = await checkEpisodeAccessPublic(targetEpisodeId);
            }
            
            if (!accessRes.data.can_access) {
              // Access denied - show locked screen
              setAccessDenied({
                ...accessRes.data,
                episode_id: targetEpisodeId,
                episode_number: episodesRes.data.find(e => e.id === targetEpisodeId)?.episode_number || 0
              });
              setLoading(false);
              return;
            }
          } catch (accessError) {
            // If access check fails for non-auth users, continue loading
            // Backend might not have public access endpoint
            console.debug('Access check error:', accessError);
          }
          
          const episodeRes = await getEpisode(targetEpisodeId);
          setCurrentEpisode(episodeRes.data);
          setAccessDenied(null); // Clear any previous denial
          
          // Save reading progress
          saveProgress(comicId, targetEpisodeId, episodeRes.data.episode_number, 0);
          
          // Load comments
          const commentsRes = await getComments({ episode_id: targetEpisodeId });
          setComments(commentsRes.data);
          
          // Track page view for revenue sharing
          if (comicRes.data?.creator_id) {
            trackEngagement({
              creator_id: comicRes.data.creator_id,
              comic_id: comicId,
              episode_id: targetEpisodeId,
              event_type: 'page_view',
              metadata: { episode_number: episodeRes.data.episode_number }
            }).catch(() => {});
          }
        }
      } catch (error) {
        console.error('Error loading reader:', error);
        toast.error('Failed to load comic');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [comicId, episodeId, isAuthenticated]);

  // Track chapter completion when user scrolls to the end
  useEffect(() => {
    if (!currentEpisode || !scrollContainerRef.current || chapterCompleted) return;
    
    const checkCompletion = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      
      const scrolledToEnd = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
      if (scrolledToEnd && !chapterCompleted) {
        setChapterCompleted(true);
        trackEvent('chapter_complete', { episode_number: currentEpisode.episode_number });
      }
    };
    
    const container = scrollContainerRef.current;
    container.addEventListener('scroll', checkCompletion);
    return () => container.removeEventListener('scroll', checkCompletion);
  }, [currentEpisode, chapterCompleted, trackEvent]);

  const handleScroll = useCallback(() => {
    const currentScrollY = scrollContainerRef.current?.scrollTop || 0;
    
    // Show controls when scrolling up, hide when scrolling down
    if (currentScrollY < lastScrollY.current || currentScrollY < 100) {
      setShowControls(true);
      
      // Reset auto-hide timer
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
      controlsTimeout.current = setTimeout(() => {
        if (currentScrollY > 100) {
          setShowControls(false);
        }
      }, 3000);
    } else if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
      setShowControls(false);
    }
    
    lastScrollY.current = currentScrollY;
  }, []);

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to like');
      navigate('/login');
      return;
    }
    
    try {
      const res = await likeEpisode(currentEpisode.id);
      setLiked(res.data.liked);
      setCurrentEpisode(prev => ({
        ...prev,
        likes: prev.likes + (res.data.liked ? 1 : -1)
      }));
      
      // Track like for revenue sharing
      if (res.data.liked) {
        trackEvent('like', { episode_number: currentEpisode.episode_number });
      }
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to comment');
      navigate('/login');
      return;
    }
    
    if (!newComment.trim()) return;
    
    try {
      const res = await createComment({
        content: newComment,
        episode_id: currentEpisode.id
      });
      setComments([res.data, ...comments]);
      setNewComment('');
      toast.success('Comment added');
      
      // Track comment for revenue sharing
      trackEvent('comment', { episode_number: currentEpisode.episode_number });
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const navigateEpisode = (direction) => {
    const currentIndex = episodes.findIndex(ep => ep.id === currentEpisode?.id);
    const newIndex = currentIndex + direction;
    
    if (newIndex >= 0 && newIndex < episodes.length) {
      navigate(`/read/${comicId}/${episodes[newIndex].id}`);
      window.location.reload();
    }
  };

  const goToEpisode = (epId) => {
    navigate(`/read/${comicId}/${epId}`);
    window.location.reload();
  };

  // Handle episode purchase from locked screen
  const handlePurchaseLockedEpisode = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }
    
    setPurchasingEpisode(true);
    try {
      const res = await createChapterCheckout(accessDenied.episode_id, window.location.origin);
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPurchasingEpisode(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show locked content screen if access denied
  if (accessDenied) {
    const DEFAULT_EPISODE_PRICE = 1.50;
    const episodePrice = accessDenied.price || DEFAULT_EPISODE_PRICE;
    
    return (
      <div className="min-h-screen bg-[var(--background)] flex flex-col" data-testid="locked-episode-page">
        {/* Header */}
        <div className="p-4 border-b border-[var(--border)]">
          <Link 
            to={`/comic/${comicId}`}
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Comic</span>
          </Link>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center page-padding">
          <div className="max-w-lg w-full">
            {/* Locked Icon */}
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-amber-500/20 to-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-500/30">
                <Lock size={48} className="text-amber-500" />
              </div>
              <h1 className="text-4xl font-teko font-bold mb-2">Episode {accessDenied.episode_number} is Locked</h1>
              <p className="text-[var(--text-secondary)]">
                Unlock this episode to continue your reading journey
              </p>
            </div>
            
            {/* Upgrade Options Card */}
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
              {/* Premium Option - Highlighted */}
              <div className="p-6 bg-gradient-to-r from-amber-500/10 to-purple-500/10 border-b border-[var(--border)]">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Crown size={24} className="text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      Upgrade to Premium
                      <span className="px-2 py-0.5 bg-amber-500 text-black text-xs font-bold rounded-full">BEST VALUE</span>
                    </h3>
                    <p className="text-[var(--text-secondary)] text-sm mt-1 mb-4">
                      Get unlimited access to all episodes across all comics. Cancel anytime.
                    </p>
                    <Link 
                      to="/pricing" 
                      className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg transition-colors"
                      data-testid="upgrade-premium-btn"
                    >
                      <Sparkles size={18} />
                      View Premium Plans
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Single Episode Purchase Option */}
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ShoppingCart size={24} className="text-green-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">Buy This Episode</h3>
                    <p className="text-[var(--text-secondary)] text-sm mt-1 mb-4">
                      One-time purchase. Access this episode forever.
                    </p>
                    <button 
                      onClick={handlePurchaseLockedEpisode}
                      disabled={purchasingEpisode}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="buy-episode-btn"
                    >
                      {purchasingEpisode ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <ShoppingCart size={18} />
                          Buy for ${episodePrice.toFixed(2)}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Sign In Prompt for Non-Authenticated Users */}
              {!isAuthenticated && (
                <div className="px-6 pb-6">
                  <div className="p-4 bg-[var(--background)] rounded-xl border border-[var(--border)]">
                    <p className="text-sm text-[var(--text-secondary)] mb-3">
                      Already have an account? Sign in to check if you have access.
                    </p>
                    <Link 
                      to="/login" 
                      className="inline-flex items-center gap-2 text-[var(--primary)] hover:underline font-medium"
                      data-testid="login-link"
                    >
                      Sign in to your account
                      <ArrowLeft size={14} className="rotate-180" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            {/* Back Link */}
            <div className="text-center mt-6">
              <Link 
                to={`/comic/${comicId}`} 
                className="text-[var(--text-secondary)] hover:text-[var(--foreground)] transition-colors"
                data-testid="back-to-comic-link"
              >
                ← Browse other episodes
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentEpisode) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center page-padding">
        <h1 className="text-2xl font-bold mb-4">No episodes found</h1>
        <Link to={`/comic/${comicId}`} className="btn-neo">
          Back to Comic
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="reader-page">
      {/* Top Controls */}
      <div 
        className={`fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black to-transparent p-4 transition-transform duration-300 ${
          showControls ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <Link 
            to={`/comic/${comicId}`}
            className="flex items-center gap-2 text-white hover:text-primary transition-colors"
            data-testid="reader-back-btn"
          >
            <ArrowLeft size={24} />
            <span className="hidden md:inline">{comic?.title}</span>
          </Link>
          
          <h1 className="font-bold text-lg truncate max-w-[200px] md:max-w-none">
            {currentEpisode.title}
          </h1>
          
          <button 
            onClick={() => setShowEpisodeList(true)}
            className="p-2 hover:text-primary transition-colors"
            data-testid="reader-episodes-btn"
          >
            <List size={24} />
          </button>
        </div>
      </div>

      {/* Panel Container */}
      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="reader-container min-h-screen overflow-y-auto hide-scrollbar pt-16 pb-32"
        data-testid="reader-panels"
      >
        {currentEpisode.panels.map((panel, index) => (
          <img 
            key={`panel-${currentEpisode.id}-${index}`}
            src={panel}
            alt={`Panel ${index + 1}`}
            className="w-full h-auto"
            loading={index > 2 ? 'lazy' : 'eager'}
            data-testid={`panel-${index}`}
          />
        ))}
        
        {/* End of Episode */}
        <div className="p-8 text-center">
          {/* Ad between episode content and navigation */}
          <ChapterEndAd comicId={comicId} episodeId={currentEpisode?.id} />
          
          {/* Support Creator Section */}
          {comic && (
            <div className="max-w-md mx-auto mb-8 p-6 bg-surface/50 border border-border rounded-xl">
              <p className="text-text-secondary text-sm mb-3">Enjoyed this chapter?</p>
              <div className="flex items-center justify-center gap-4">
                <TipButton 
                  creatorId={comic.creator_id}
                  creatorName={comic.creator_name}
                  comicId={comicId}
                  variant="compact"
                />
                <SuperLikeButton
                  creatorId={comic.creator_id}
                  creatorName={comic.creator_name}
                  comicId={comicId}
                  episodeId={currentEpisode?.id}
                />
              </div>
            </div>
          )}
          
          <p className="text-text-secondary mb-4">End of {currentEpisode.title}</p>
          <div className="flex gap-4 justify-center">
            {episodes.findIndex(ep => ep.id === currentEpisode.id) > 0 && (
              <button 
                onClick={() => navigateEpisode(-1)}
                className="btn-neo text-sm"
                data-testid="prev-episode-btn"
              >
                Previous Episode
              </button>
            )}
            {episodes.findIndex(ep => ep.id === currentEpisode.id) < episodes.length - 1 && (
              <button 
                onClick={() => navigateEpisode(1)}
                className="btn-neo text-sm"
                data-testid="next-episode-btn"
              >
                Next Episode
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black to-transparent p-4 transition-transform duration-300 ${
          showControls ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="flex items-center justify-center gap-8 max-w-4xl mx-auto">
          <button
            onClick={() => navigateEpisode(-1)}
            disabled={episodes.findIndex(ep => ep.id === currentEpisode.id) === 0}
            className="p-2 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="nav-prev-btn"
          >
            <ChevronUp size={28} />
          </button>
          
          <button
            onClick={handleLike}
            className={`flex items-center gap-2 p-2 transition-colors ${liked ? 'text-secondary' : 'hover:text-secondary'}`}
            data-testid="like-btn"
          >
            <Heart size={24} fill={liked ? 'currentColor' : 'none'} />
            <span>{currentEpisode.likes}</span>
          </button>
          
          <button
            onClick={() => setShowComments(true)}
            className="flex items-center gap-2 p-2 hover:text-primary transition-colors"
            data-testid="comments-btn"
          >
            <MessageSquare size={24} />
            <span>{comments.length}</span>
          </button>
          
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              toast.success('Link copied!');
            }}
            className="p-2 hover:text-primary transition-colors"
            data-testid="share-btn"
          >
            <Share2 size={24} />
          </button>
          
          <button
            onClick={() => navigateEpisode(1)}
            disabled={episodes.findIndex(ep => ep.id === currentEpisode.id) === episodes.length - 1}
            className="p-2 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="nav-next-btn"
          >
            <ChevronDown size={28} />
          </button>
        </div>
      </div>

      {/* Episode List Drawer */}
      {showEpisodeList && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" data-testid="episode-drawer">
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-surface border-l border-border overflow-y-auto">
            <div className="sticky top-0 bg-surface border-b border-border p-4 flex items-center justify-between">
              <h2 className="font-bold text-lg">Episodes</h2>
              <button onClick={() => setShowEpisodeList(false)} data-testid="close-episodes-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-4 space-y-2">
              {episodes.map((ep) => (
                <button
                  key={ep.id}
                  onClick={() => goToEpisode(ep.id)}
                  className={`w-full text-left p-4 border transition-colors ${
                    ep.id === currentEpisode.id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`episode-item-${ep.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Ep. {ep.episode_number}</span>
                    {!ep.is_free && <span className="badge-new">Premium</span>}
                  </div>
                  <p className="text-sm text-text-secondary mt-1">{ep.title}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Comments Drawer */}
      {showComments && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" data-testid="comments-drawer">
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-surface border-l border-border flex flex-col">
            <div className="sticky top-0 bg-surface border-b border-border p-4 flex items-center justify-between">
              <h2 className="font-bold text-lg">Comments ({comments.length})</h2>
              <button onClick={() => setShowComments(false)} data-testid="close-comments-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {comments.length === 0 ? (
                <p className="text-text-secondary text-center py-8">No comments yet. Be the first!</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="border border-border p-4" data-testid={`comment-${comment.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-surface-highlight overflow-hidden">
                        {comment.profile_picture ? (
                          <img src={comment.profile_picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-text-secondary">
                            {comment.username?.[0]?.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{comment.username}</p>
                        <p className="text-xs text-text-secondary">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm">{comment.content}</p>
                  </div>
                ))
              )}
            </div>
            
            <form onSubmit={handleComment} className="p-4 border-t border-border">
              <div className="flex gap-2">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 bg-surface-highlight border-border"
                  data-testid="comment-input"
                />
                <Button type="submit" className="btn-neo px-4 py-2" data-testid="submit-comment-btn">
                  Post
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReaderPage;
